import { useLayoutEffect, useRef, useState } from "react";

/** يقيس عرض الحاوية ويتابع تغيّره، ليُبنى الجدول على العرض المتاح فعلياً */
export const useContainerWidth = () => {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // useLayoutEffect حتى يُقاس العرض قبل أول رسم فلا يومض الجدول
  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return undefined;

    setContainerWidth(element.clientWidth);

    if (typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
    });

    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  return { containerRef, containerWidth };
};

/**
 * يوزّع الأعمدة على العرض المتاح: ما يتّسع يبقى في الجدول،
 * والباقي يُعاد كـ hidden ليُعرض داخل الصف الموسّع (علامة +).
 *
 * @param {Array<{key: string, width: number, priority: number}>} fields
 * @param {number} containerWidth العرض المتاح بالبكسل (0 قبل القياس)
 * @param {{reservedWidth?: number, minVisible?: number}} options
 *   reservedWidth: عرض محجوز للأعمدة الثابتة (زر التوسيع + أعمدة الإجراءات)
 *   minVisible: أقل عدد أعمدة تُعرض مهما ضاقت الشاشة
 * @returns {{visibleFields: Array, hiddenFields: Array, tight: boolean}}
 *   tight: العرض لا يكفي حتى للحد الأدنى — على المستدعي إسقاط الأعرضة الثابتة
 *   وترك المتصفح يوزّع المساحة مع قص النص (ellipsis) بدل السكرول الأفقي.
 */
export const splitColumns = (
  fields,
  containerWidth,
  { reservedWidth = 0, minVisible = 2 } = {},
) => {
  if (!containerWidth) {
    return { visibleFields: fields, hiddenFields: [], tight: false };
  }

  // نرتّب حسب الأولوية لاختيار الأهم، ثم نعيد الترتيب الأصلي عند العرض
  const byPriority = [...fields].sort((a, b) => a.priority - b.priority);
  const kept = new Set();
  let used = reservedWidth;

  byPriority.forEach((field) => {
    const fits = used + field.width <= containerWidth;

    if (fits || kept.size < minVisible) {
      kept.add(field.key);
      used += field.width;
    }
  });

  return {
    visibleFields: fields.filter((field) => kept.has(field.key)),
    hiddenFields: fields.filter((field) => !kept.has(field.key)),
    tight: used > containerWidth,
  };
};

/**
 * ارتفاع الشريط العلوي الثابت، ليلتصق رأس الجدول تحته مباشرة
 * أثناء تمرير الصفحة (بدل سكرول داخلي للجدول).
 */
export const useStickyHeaderOffset = () => {
  const [offset, setOffset] = useState(0);

  useLayoutEffect(() => {
    const header = document.querySelector(".header-custom");
    if (!header) return undefined;

    const update = () => setOffset(header.getBoundingClientRect().height);

    update();

    if (typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver(update);

    observer.observe(header);

    return () => observer.disconnect();
  }, []);

  return offset;
};
